import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig, testimonials } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = `关于我们 - ${siteConfig.name}`;
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    setFormStatus('loading');
    setFormError('');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9q32sfnahb7382ov5eg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }
      if (response.ok && parsed) {
        const code = (parsed as Record<string, unknown>).code;
        if (code === 'OK') {
          setFormStatus('success');
          form.reset();
        } else {
          const serverMsg =
            ((parsed as Record<string, unknown>).meta as Record<string, string>)?.message ||
            ((parsed as Record<string, unknown>).message as string) ||
            responseText;
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      } else {
        setFormStatus('error');
        setFormError('提交失败，请检查网络后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于${siteConfig.name}`,
            description: `了解${siteConfig.name}的品牌故事、团队理念和联系方式`,
            url: `${siteConfig.url}/about`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '白丝时尚网是什么？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '白丝时尚网是一个专注于白色丝袜时尚穿搭、产品评测与潮流资讯的中文内容平台，致力于为爱美女性提供最专业的白丝穿搭建议。',
                },
              },
              {
                '@type': 'Question',
                name: '如何选择适合自己的白丝？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '选择白丝时需要根据肤色、腿型和使用场景综合考虑。冷白皮适合纯白色，暖黄皮建议选择米白或奶白色。不同腿型适合不同厚度和纹理的白丝款式。',
                },
              },
              {
                '@type': 'Question',
                name: '白丝应该如何保养？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '白丝建议手洗，用30℃以下温水和中性洗涤剂，阴凉通风处晾干。收纳时应卷起而非折叠，最好放在独立收纳袋中防止勾丝。',
                },
              },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Page Header */}
        <section className="relative w-full h-[240px] md:h-[300px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20fashion%20studio%20workspace%20with%20soft%20natural%20lighting%2C%20white%20stockings%20fashion%20sketches%20on%20wooden%20desk%2C%20cream%20and%20beige%20color%20palette%2C%20artistic%20editorial%20style%2C%20warm%20cozy%20atmosphere&width=1800&height=600&seq=about-header-01&orientation=landscape"
              alt="关于我们"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/15 to-black/15"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">关于我们</h1>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Brand Story */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-3 text-sm md:text-base">
                  白丝时尚网成立于2024年，是国内领先的白色丝袜时尚内容平台。我们的创始团队由资深时尚编辑、专业造型师和资深电商从业者组成，对丝袜时尚有着深厚的热爱和专业理解。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-3 text-sm md:text-base">
                  我们相信，白丝不仅仅是穿搭单品，更是一种生活态度的表达。无论你是职场精英、时尚达人，还是追求精致生活的普通女性，都能在这里找到适合自己的白丝穿搭灵感和实用建议。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  自成立以来，白丝时尚网已累计发布<strong className="text-foreground-900">超过200篇</strong>原创文章，服务了<strong className="text-foreground-900">数十万</strong>追求美丽的女性读者。我们的愿景是成为中文世界最值得信赖的白丝时尚指南。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Two%20fashionable%20Chinese%20women%20editors%20working%20together%20in%20bright%20modern%20creative%20studio%20with%20fashion%20magazine%20layout%20on%20table%2C%20white%20stockings%20samples%20displayed%2C%20creative%20workspace%2C%20warm%20natural%20light%2C%20lifestyle%20editorial%20photography&width=800&height=600&seq=about-story-01&orientation=landscape"
                  alt="团队工作"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-8 text-center">
              我们的理念
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-heart-line',
                  title: '热爱时尚',
                  desc: '我们热爱丝袜时尚，致力于发掘和分享最美的白丝穿搭灵感，让每一位读者都能找到属于自己的风格。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '专业可靠',
                  desc: '每一篇文章都经过资深编辑精心打磨，产品评测客观真实，穿搭建议专业实用，是你值得信赖的时尚伙伴。',
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '以读者为本',
                  desc: '我们始终以读者需求为导向，持续创作有价值的内容，帮助每一位女性更加自信、优雅地展现自我。',
                },
              ].map((item, index) => (
                <div key={index} className="bg-background-50 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${item.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-8 text-center">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t, index) => (
                <div key={index} className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatarUrl}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-foreground-900 text-sm">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                  如果你有任何问题、建议或合作意向，欢迎通过以下方式联系我们，我们会在24小时内回复。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-mail-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm text-foreground-900">{siteConfig.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-phone-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电话</p>
                      <p className="text-sm text-foreground-900">{siteConfig.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm text-foreground-900">{siteConfig.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-50 rounded-lg p-5 md:p-6">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">发送消息</h3>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-readdy-form="">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 text-foreground-900"
                        placeholder="请输入你的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 text-foreground-900"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1">留言</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 text-foreground-900 resize-none"
                        placeholder="请输入你的留言（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      name="company_alt"
                      type="text"
                      className="contact-alt-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full px-5 py-2.5 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">消息已发送，我们会尽快回复你！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}